import { AlertInfoDTO, AlertStatus } from '@app/alerts/dto';
import { useCheckAuth } from '@app/auth/hooks/use-check-auth';
import { RESOURCES } from '@constants';
import { useImpersonation } from '@contexts/impersonation-context';
import { useList } from '@refinedev/core';
import { createContext, PropsWithChildren, useContext, useEffect, useMemo } from 'react';

type AlertsContextType = {
  alerts: AlertInfoDTO[] | undefined;
  totalCount: number;
  totalActiveCount: number;
  isLoading: boolean;
  refetch: () => void;
  isError: boolean;
};

export const AlertsContext = createContext<AlertsContextType>({
  alerts: [],
  totalCount: 0,
  totalActiveCount: 0,
  isLoading: false,
  refetch: () => {},
  isError: false,
});

export const AlertsContextProvider = ({ children }: PropsWithChildren) => {
  const { userIsValid } = useCheckAuth({});
  const { isImpersonating } = useImpersonation();

  const {
    result,
    query: { isLoading, refetch, isError },
  } = useList<AlertInfoDTO>({
    resource: RESOURCES.alerts,
    queryOptions: { queryKey: [], enabled: false },
  });

  useEffect(() => {
    if (userIsValid) refetch(); //we do not want to send request if the logged in user is invalid
  }, [userIsValid, refetch, isImpersonating]);

  const alerts = useMemo(() => (result?.data || []) as AlertInfoDTO[], [result]);

  const totalActiveCount = useMemo(
    () => alerts?.filter(({ status }) => status === AlertStatus.Active).length,
    [alerts],
  );

  return (
    <AlertsContext.Provider
      value={{
        alerts,
        totalCount: alerts.length,
        totalActiveCount,
        isError,
        isLoading,
        refetch,
      }}
    >
      {children}
    </AlertsContext.Provider>
  );
};

export const useAlerts = () => useContext(AlertsContext);
